import React, { useCallback, useEffect, useState } from "react";
import { FormControl, FormControlLabel, Grid, Radio, RadioGroup, Stack, TextField, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../store";
import { setPreferencesPartial } from "../../store/expertise/expertise.reducer";
import { Title } from "../shared/Texts";
import { EnvoiSyntheseTleMode } from "../../../core/apis/monsisra/generated";
import { MssAdresseValidator } from "../../../core/validators/mss.adresse.validator";
import { getLabelFromMode, getModeEnvoiSyntheseTle } from "../../../core/helpers/preferencesEnvoiSyntheseTleHelper";

export default function EnvoiSyntheseMSS() {
	const dispatch = useAppDispatch();
	const { preferences } = useAppSelector((s) => s.expertise);
	const [adresseError, setAdresseError] = useState(false);

	const preferencesEnvoiSyntheseTleExpert = preferences?.preferencesEnvoiSyntheseTleExpert;
	const mode = getModeEnvoiSyntheseTle(preferencesEnvoiSyntheseTleExpert);

	useEffect(() => {
		if (!preferences?.preferencesEnvoiSyntheseTleExpert) {
			dispatch(
				setPreferencesPartial({
					preferencesEnvoiSyntheseTleExpert: {
						modeEnvoiMss: EnvoiSyntheseTleMode.TO_EXPERT_SOLLICITE,
					},
				})
			);
		}
	}, [dispatch, preferences?.preferencesEnvoiSyntheseTleExpert]);

	const onRadioChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value as EnvoiSyntheseTleMode;
			dispatch(
				setPreferencesPartial({
					preferencesEnvoiSyntheseTleExpert: {
						modeEnvoiMss: value,
						// Conserver l'adresse tierce même si on change de mode
						mssTierce: preferencesEnvoiSyntheseTleExpert?.mssTierce,
					},
				})
			);
			if (value !== EnvoiSyntheseTleMode.TO_TIERCE) {
				setAdresseError(false);
			}
		},
		[dispatch, preferencesEnvoiSyntheseTleExpert?.mssTierce]
	);

	const onAdresseChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value ?? "";
			dispatch(
				setPreferencesPartial({
					preferencesEnvoiSyntheseTleExpert: {
						// Conserve le mode même lors de la saisie de l'adresse
						modeEnvoiMss: mode,
						mssTierce: value,
					},
				})
			);
			if (mode === EnvoiSyntheseTleMode.TO_TIERCE) {
				setAdresseError(!MssAdresseValidator.validate(value));
			}
		},
		[dispatch, mode]
	);

	if (!preferences) return null;

	return (
		<Grid container direction={"column"} spacing={2}>
			<Grid item>
				<Title text={"Envoi automatique de la synthèse"} />
			</Grid>
			<Grid item>
				<Stack>
					<Typography fontStyle={"italic"} fontSize={"smaller"}>
						{
							"À la finalisation de chaque téléexpertise, une synthèse est générée. Vous pouvez choisir d'envoyer automatiquement cette synthèse à une adresse MSS, notamment dans un but d'intégration dans votre DPI."
						}
					</Typography>
				</Stack>
			</Grid>
			<Grid item flex={1}>
				<FormControl component="fieldset" fullWidth>
					<RadioGroup value={mode} onChange={onRadioChange} name="radio-buttons-group-envoi-synthese">
						<FormControlLabel value={EnvoiSyntheseTleMode.NE_PAS_ENVOYER} control={<Radio />} label={getLabelFromMode(EnvoiSyntheseTleMode.NE_PAS_ENVOYER)} />
						<FormControlLabel value={EnvoiSyntheseTleMode.TO_EXPERT_SOLLICITE} control={<Radio />} label={getLabelFromMode(EnvoiSyntheseTleMode.TO_EXPERT_SOLLICITE)} />
						<FormControlLabel value={EnvoiSyntheseTleMode.TO_EXPERT_FINAL} control={<Radio />} label={getLabelFromMode(EnvoiSyntheseTleMode.TO_EXPERT_FINAL)} />
						<FormControlLabel
							slotProps={{
								typography: {
									flex: 1,
								},
							}}
							value={EnvoiSyntheseTleMode.TO_TIERCE}
							control={<Radio />}
							label={
								<Stack alignItems={"center"} direction={"row"} spacing={1}>
									<Typography flexShrink={0}>{getLabelFromMode(EnvoiSyntheseTleMode.TO_TIERCE)}</Typography>
									<TextField
										className={"input"}
										variant="standard"
										fullWidth
										value={preferencesEnvoiSyntheseTleExpert?.mssTierce ?? ""}
										onChange={onAdresseChange}
										disabled={mode !== EnvoiSyntheseTleMode.TO_TIERCE}
										error={adresseError}
										helperText={adresseError ? "Adresse MSS invalide" : undefined}
										onClick={(e) => e.stopPropagation()}
										FormHelperTextProps={{ sx: { position: "absolute", bottom: -20, right: 0 } }}
									/>
								</Stack>
							}
						/>
					</RadioGroup>
				</FormControl>
			</Grid>
		</Grid>
	);
}
